import React, { useState } from 'react';
import { useOffline } from '../contexts/OfflineContext';
import { usePWA } from '../hooks/usePWA';
import { Wifi, WifiOff, RefreshCw, Settings, X, AlertTriangle, CheckCircle, Clock, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const OfflineIndicator = () => {
  const { 
    isOnline, 
    pendingActions, 
    syncing, 
    lastSyncTime, 
    syncErrors,
    autoSyncEnabled,
    syncInterval,
    manualSync, 
    getPendingCount, 
    clearOfflineData,
    setAutoSyncEnabled,
    setSyncInterval
  } = useOffline();

  const { canInstall, installApp, registration } = usePWA();

  const [showDetails, setShowDetails] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const pendingCount = getPendingCount();

  // Zaman formatı
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Hiç';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Az önce';
    if (diffInMinutes < 60) return `${diffInMinutes} dakika önce`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} saat önce`;
    return time.toLocaleDateString('tr-TR');
  };

  // Senkronizasyon durumu
  const getSyncStatus = () => {
    if (syncing) return { text: 'Senkronize ediliyor...', color: 'text-indigo-600', icon: RefreshCw };
    if (syncErrors.length > 0) return { text: `${syncErrors.length} hata`, color: 'text-red-600', icon: AlertTriangle };
    if (pendingCount > 0) return { text: `${pendingCount} bekleyen`, color: 'text-orange-600', icon: Clock };
    return { text: 'Güncel', color: 'text-green-600', icon: CheckCircle };
  };

  const syncStatus = getSyncStatus();

  if (isOnline && pendingCount === 0 && !showDetails && !showSettings) {
    return null; // Online ve bekleyen işlem yoksa gizle
  }

  return (
    <>
      {/* Ana İndikatör */}
      <div className="fixed bottom-4 right-4 z-50">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[280px]">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              {isOnline ? (
                <Wifi className="h-4 w-4 text-green-600" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-600" />
              )}
              <span className="text-sm font-medium">
                {isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Ayarlar"
              >
                <Settings className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Detaylar"
              >
                {showDetails ? <X className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Durum Bilgisi */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <syncStatus.icon className={`h-4 w-4 ${syncStatus.color}`} />
              <span className={`text-sm ${syncStatus.color}`}>
                {syncStatus.text}
              </span>
            </div>
            {pendingCount > 0 && (
              <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                {pendingCount}
              </span>
            )}
          </div>

          {/* Son Senkronizasyon */}
          {lastSyncTime && (
            <div className="text-xs text-gray-500 mt-1">
              Son senkronizasyon: {formatTime(lastSyncTime)}
            </div>
          )}

          {/* Detaylar */}
          {showDetails && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="space-y-2">
                {/* Bekleyen İşlemler */}
                {pendingActions.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-700 mb-1">Bekleyen İşlemler:</h4>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {pendingActions.slice(0, 5).map((action) => (
                        <div key={action.id} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                          <div className="font-medium">{action.method} {action.url}</div>
                          <div className="text-gray-500">
                            {formatTime(action.timestamp)} • Deneme: {action.retryCount}/3
                          </div>
                        </div>
                      ))}
                      {pendingActions.length > 5 && (
                        <div className="text-xs text-gray-500">
                          ... ve {pendingActions.length - 5} işlem daha
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Hatalar */}
                {syncErrors.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-red-700 mb-1">Hatalar:</h4>
                    <div className="max-h-24 overflow-y-auto space-y-1">
                      {syncErrors.slice(0, 3).map((error, index) => (
                        <div key={index} className="text-xs text-red-600 bg-red-50 p-2 rounded">
                          {error.action.method} {error.action.url}
                          <div className="text-red-500">{error.error}</div>
                        </div>
                      ))}
                      {syncErrors.length > 3 && (
                        <div className="text-xs text-red-500">
                          ... ve {syncErrors.length - 3} hata daha
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Aksiyonlar */}
                <div className="flex space-x-2 pt-2">
                  <button
                    onClick={manualSync}
                    disabled={syncing || !isOnline}
                    className="flex-1 bg-indigo-600 text-white text-xs px-3 py-1 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncing ? 'Senkronize...' : 'Senkronize Et'}
                  </button>
                  <button
                    onClick={clearOfflineData}
                    className="bg-red-600 text-white text-xs px-3 py-1 rounded hover:bg-red-700"
                  >
                    Temizle
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Ayarlar */}
          {showSettings && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Senkronizasyon Ayarları</h4>
              <div className="space-y-2">
                {/* Otomatik Senkronizasyon */}
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={autoSyncEnabled}
                    onChange={(e) => setAutoSyncEnabled(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-xs">Otomatik senkronizasyon</span>
                </label>

                {/* Senkronizasyon Aralığı */}
                <div>
                  <label className="text-xs text-gray-600">Senkronizasyon aralığı:</label>
                  <select
                    value={syncInterval}
                    onChange={(e) => setSyncInterval(Number(e.target.value))}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 mt-1"
                  >
                    <option value={10000}>10 saniye</option>
                    <option value={30000}>30 saniye</option>
                    <option value={60000}>1 dakika</option>
                    <option value={300000}>5 dakika</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OfflineIndicator;

